"use client";

import { useState } from "react";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/button";
import { Heading } from "@/components/atoms/Heading";
import { Link } from "@/components/atoms/Link";
import { Text } from "@/components/atoms/Text";
import { useConsent } from "./ConsentProvider";
import {
    clearTrackingCookies,
    COOKIE_SETTINGS_ANCHOR,
    reloadPage,
    scheduleTrackingCookieCleanup,
} from "./privacyChoices";

/**
 * Your Privacy Choices — the opt-out form the footer's "Your Privacy Choices"
 * link leads to (CCPA regs §7015(c)), plus cookie settings so visitors can
 * change or withdraw consent at any time (§7004(a)(5)). Shows whether a
 * Global Privacy Control signal was processed (§7025(c)(6)).
 */
export function PrivacyChoicesPanel() {
    const { marketingConsent, analyticsConsent, gpcDetected, isLoading } = useConsent();
    const [saved, setSaved] = useState(false);

    if (isLoading) return null;

    return (
        <Box className="space-y-10">
            <Box role="note" className="rounded-lg border border-border bg-muted p-5">
                {gpcDetected ? (
                    <Text as="p" className="font-semibold">
                        Opt-out preference signal honored. Your browser sends a Global Privacy Control
                        signal, so we treat it as a request to opt out of the sale or sharing of your
                        personal information and do not load advertising cookies.
                    </Text>
                ) : (
                    <Text as="p">
                        No Global Privacy Control signal was detected from your browser. You can opt out
                        below, or turn on Global Privacy Control in a supporting browser or extension.
                    </Text>
                )}
            </Box>

            {/* Remount when stored consent changes (e.g. the banner is answered
                on this page) so the toggles never show a stale choice. */}
            <CookieSettingsForm
                key={`${marketingConsent}|${analyticsConsent}`}
                saved={saved}
                onSavedChange={setSaved}
            />

            <Box className="space-y-3">
                <Heading level={2}>Other privacy requests</Heading>
                <Text as="p">
                    To ask what personal information we hold about you, or to have it corrected or
                    deleted, email{" "}
                    <Link href="mailto:info@agencyq.com" className="underline underline-offset-2">
                        info@agencyq.com
                    </Link>
                    . For details on what we collect and why, see our{" "}
                    <Link href="/privacy-policy" className="underline underline-offset-2">
                        Privacy Policy
                    </Link>
                    .
                </Text>
            </Box>
        </Box>
    );
}

function CookieSettingsForm({
    saved,
    onSavedChange,
}: {
    saved: boolean;
    onSavedChange: (saved: boolean) => void;
}) {
    const { marketingConsent, analyticsConsent, gpcDetected, setConsent } = useConsent();
    const [advertising, setAdvertising] = useState(marketingConsent === "granted");
    const [analytics, setAnalytics] = useState(analyticsConsent === "granted");

    const save = () => {
        const revoking =
            (marketingConsent === "granted" && !advertising) || (analyticsConsent === "granted" && !analytics);
        setConsent(advertising && !gpcDetected, analytics);
        onSavedChange(true);
        if (revoking) {
            clearTrackingCookies();
            scheduleTrackingCookieCleanup();
            reloadPage();
        }
    };

    return (
        <form
            id={COOKIE_SETTINGS_ANCHOR}
            className="scroll-mt-32 space-y-6"
            onSubmit={(event) => {
                event.preventDefault();
                save();
            }}
        >
            <Heading level={2}>Cookie settings</Heading>

            <label className="flex items-start gap-3">
                <input
                    type="checkbox"
                    className="mt-1 size-5 shrink-0 accent-primary"
                    checked={advertising && !gpcDetected}
                    disabled={gpcDetected}
                    onChange={(event) => {
                        setAdvertising(event.target.checked);
                        onSavedChange(false);
                    }}
                />
                <Text className="space-y-1">
                    <Text className="block font-semibold">
                        Advertising cookies (sale or sharing of personal information)
                    </Text>
                    <Text className="block text-sm text-muted-foreground">
                        Lets our advertising partners — Microsoft Advertising, LinkedIn, Google Ads and
                        ZoomInfo — receive information about your visit to measure and target ads.
                        Unchecking this opts you out of the sale or sharing of your personal information.
                    </Text>
                </Text>
            </label>

            <label className="flex items-start gap-3">
                <input
                    type="checkbox"
                    className="mt-1 size-5 shrink-0 accent-primary"
                    checked={analytics}
                    onChange={(event) => {
                        setAnalytics(event.target.checked);
                        onSavedChange(false);
                    }}
                />
                <Text className="space-y-1">
                    <Text className="block font-semibold">Analytics cookies</Text>
                    <Text className="block text-sm text-muted-foreground">
                        Helps us understand how the site is used, through Google Analytics and
                        Salesforce, which process this data on our behalf.
                    </Text>
                </Text>
            </label>

            <Box className="flex flex-wrap items-center gap-4">
                <Button type="submit">Save my choices</Button>
                <Text role="status" aria-live="polite" className="text-sm text-muted-foreground">
                    {saved ? "Your choices have been saved." : ""}
                </Text>
            </Box>
        </form>
    );
}
